// Embedded external forms.
//
// The form itself is the server-rendered /<tenant>/forms/<code>/?embed=1 page
// in an iframe — it owns field rendering, validation, the honeypot, signature
// pads, and posts `spa-form-resize` / `spa-form-submitted` messages to the
// parent for auto-height. This module is only the host-side plumbing:
//   - mountFormEmbed(el, apiBase, code)  → inline iframe that tracks content height
//   - openFormModal(apiBase, code)       → centered modal with the same iframe
// Host markup (wired up in widget.ts):
//   <div data-calendoula-form="CODE"></div>
//   <button data-calendoula-form-button="CODE">Give feedback</button>

const STYLE_ID = 'spa-form-embed-styles';

const CSS = `
.pp-form-embed { width: 100%; }
.pp-form-embed iframe, .pp-form-modal iframe {
  display: block; width: 100%; border: none; min-height: 220px;
  transition: height 0.15s ease;
}
.pp-form-frame-wrap { position: relative; }
.pp-form-loading {
  position: absolute; inset: 0; background: #fff; z-index: 1;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  opacity: 1; transition: opacity 0.2s ease;
}
.pp-form-loading.done { opacity: 0; pointer-events: none; }
.pp-form-loading-logo { width: 44px; height: 44px; animation: pp-form-breathe 1.8s ease-in-out infinite; }
.pp-form-loading-line { color: #8a7f76; font-size: 13.5px; margin: 0; }
.pp-form-spin {
  display: inline-block; width: 14px; height: 14px; flex: none;
  border: 2px solid #e8ddd3; border-top-color: #b8623e; border-radius: 50%;
  animation: pp-form-spin 1.4s linear infinite;
}
@keyframes pp-form-breathe { 0%, 100% { opacity: 0.55; transform: scale(0.94); } 50% { opacity: 1; transform: scale(1); } }
@keyframes pp-form-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .pp-form-loading-logo, .pp-form-spin { animation: none; } }
.pp-form-backdrop {
  position: fixed; inset: 0; background: rgba(30, 24, 19, 0.45);
  z-index: 100000; opacity: 0; transition: opacity 0.2s ease;
}
.pp-form-backdrop.visible { opacity: 1; }
.pp-form-modal {
  position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
  width: min(600px, calc(100vw - 32px)); max-height: calc(100vh - 48px);
  background: #fff; border-radius: 12px; box-shadow: 0 12px 40px rgba(0,0,0,0.25);
  z-index: 100001; overflow-y: auto; -webkit-overflow-scrolling: touch;
}
.pp-form-modal-close {
  position: absolute; top: 10px; right: 10px; width: 32px; height: 32px;
  border: none; border-radius: 50%; background: rgba(0,0,0,0.06); color: #555;
  font-size: 18px; line-height: 32px; text-align: center; cursor: pointer; padding: 0;
  z-index: 1; font-family: system-ui, sans-serif;
}
.pp-form-modal-close:hover { background: rgba(0,0,0,0.12); }
@media (max-width: 640px) {
  .pp-form-modal { width: calc(100vw - 16px); max-height: calc(100vh - 24px); }
}
`;

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

export function formPageUrl(apiBase: string, code: string, font = ''): string {
  const f = font ? `&font=${encodeURIComponent(font.slice(0, 300))}` : '';
  return `${(apiBase || '').replace(/\/+$/, '')}/forms/${encodeURIComponent(code)}/?embed=1${f}`;
}

/** The website's font for the form (Tim, 2026-11-03): an iframe never inherits
 *  the page's CSS, so the font where the form sits is handed to the form page,
 *  which uses it (and loads it from Google Fonts when it is one).
 *  `data-font="Lora, serif"` names one; `data-font="default"` keeps the form's own. */
export function hostFont(el: Element | null): string {
  if (!el) return '';
  const set = (el.getAttribute('data-font') || '').trim();
  if (set) return set.toLowerCase() === 'default' ? '' : set;
  try { return getComputedStyle(el).fontFamily || ''; } catch { return ''; }
}

interface TrackedFrame {
  frame: HTMLIFrameElement;
  origin: string;
  onSubmitted?: () => void;
  /** called on the first message from the form page (it has really rendered) */
  onReady?: () => void;
}

const tracked: TrackedFrame[] = [];
let listening = false;

function ensureListener(): void {
  if (listening) return;
  listening = true;
  window.addEventListener('message', (e: MessageEvent) => {
    const data = e.data as { type?: string; height?: number } | null;
    if (!data || (data.type !== 'spa-form-resize' && data.type !== 'spa-form-submitted')) return;
    for (const t of tracked) {
      if (t.frame.contentWindow !== e.source) continue;
      if (t.origin && e.origin !== t.origin) continue;
      const h = Number(data.height);
      if (h > 0) t.frame.style.height = `${Math.ceil(h)}px`;
      if (t.onReady) { t.onReady(); t.onReady = undefined; }
      if (data.type === 'spa-form-submitted') t.onSubmitted?.();
    }
  });
}

function buildFrame(apiBase: string, code: string, onSubmitted?: () => void, font = ''): HTMLIFrameElement {
  const url = formPageUrl(apiBase, code, font);
  const frame = document.createElement('iframe');
  frame.src = url;
  frame.loading = 'lazy';
  frame.title = 'Form';
  let origin = '';
  try { origin = new URL(url, window.location.href).origin; } catch { /* keep '' → skip origin check */ }
  tracked.push({ frame, origin, onSubmitted });
  ensureListener();
  return frame;
}

/**
 * The iframe + a loading overlay (spinner + shimmer rows) on top of it. The
 * overlay fades out on the form page's first postMessage (it has really
 * rendered), with the iframe's own `load` event as the fallback — that fires
 * even for an error page, so the host never spins forever.
 */
function buildFrameWrap(apiBase: string, code: string, onSubmitted?: () => void, font = ''): { wrap: HTMLElement; frame: HTMLIFrameElement } {
  const frame = buildFrame(apiBase, code, onSubmitted, font);
  const wrap = document.createElement('div');
  wrap.className = 'pp-form-frame-wrap';
  const overlay = document.createElement('div');
  overlay.className = 'pp-form-loading';
  overlay.setAttribute('aria-busy', 'true');
  // The Calendoula mark breathing over one line — same look as the pre-init
  // embed placeholders; spinner fallback when the apiBase has no origin.
  let logo = '';
  try { logo = `${new URL(formPageUrl(apiBase, code), window.location.href).origin}/static/calendoula-logo-sm.png`; } catch { /* spinner */ }
  overlay.innerHTML = (logo
    ? `<img class="pp-form-loading-logo" src="${logo}" alt="" width="44" height="44">`
    : '<span class="pp-form-spin"></span>')
    + '<p class="pp-form-loading-line">Loading form…</p>';
  const done = () => {
    if (!overlay.parentNode) return;
    overlay.classList.add('done');
    setTimeout(() => overlay.remove(), 250);
  };
  const t = tracked.find(x => x.frame === frame);
  if (t) t.onReady = done;
  frame.addEventListener('load', () => {
    // Chrome fires a load event for the iframe's initial about:blank document —
    // that one must NOT clear the overlay (the real page hasn't even been
    // requested yet). The real form page is cross-origin, so reading its
    // location throws — which is exactly the signal a real document arrived.
    try {
      if (frame.contentWindow && frame.contentWindow.location.href === 'about:blank') return;
    } catch { /* cross-origin: a real document loaded */ }
    done();
  });
  wrap.appendChild(frame);
  wrap.appendChild(overlay);
  return { wrap, frame };
}

function untrack(frame: HTMLIFrameElement): void {
  const i = tracked.findIndex(t => t.frame === frame);
  if (i >= 0) tracked.splice(i, 1);
}

/** Inline embed: render the form into a host element on the page. */
export function mountFormEmbed(el: HTMLElement, apiBase: string, code: string): void {
  ensureStyles();
  el.classList.add('pp-form-embed');
  const font = hostFont(el);          // read before the placeholder is replaced
  el.innerHTML = '';
  el.appendChild(buildFrameWrap(apiBase, code, undefined, font).wrap);
}

let modalEls: { backdrop: HTMLElement; modal: HTMLElement; frame: HTMLIFrameElement } | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;

/** Modal embed: open the form as a centered dialog over the host page. */
export function openFormModal(apiBase: string, code: string, font = hostFont(document.body)): void {
  closeFormModal();
  ensureStyles();

  const backdrop = document.createElement('div');
  backdrop.className = 'pp-form-backdrop';
  backdrop.addEventListener('click', () => closeFormModal());

  const modal = document.createElement('div');
  modal.className = 'pp-form-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');

  const close = document.createElement('button');
  close.className = 'pp-form-modal-close';
  close.setAttribute('aria-label', 'Close');
  close.innerHTML = '&#10005;';
  close.addEventListener('click', () => closeFormModal());

  const { wrap, frame } = buildFrameWrap(apiBase, code, undefined, font);
  modal.appendChild(close);
  modal.appendChild(wrap);
  document.body.appendChild(backdrop);
  document.body.appendChild(modal);
  requestAnimationFrame(() => backdrop.classList.add('visible'));

  escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeFormModal(); };
  document.addEventListener('keydown', escHandler);
  modalEls = { backdrop, modal, frame };
}

export function closeFormModal(): void {
  if (!modalEls) return;
  untrack(modalEls.frame);
  modalEls.backdrop.remove();
  modalEls.modal.remove();
  modalEls = null;
  if (escHandler) {
    document.removeEventListener('keydown', escHandler);
    escHandler = null;
  }
}
